import { ref, computed, type Ref, type reactive } from "vue";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
} from "@/common/localStorageUtil";
import { getWordList } from "@/common/graphql/expression";
import { getChainList } from "@/common/graphql/chain";
import { getShopList } from "@/common/graphql/shop";
import searchConditionStore from "@/store/searchConditionStore";
import type { ShopListInput } from "@/generated/graphql";
import type { ChainItem, ShopSelect } from "../types";
import { SHOP_LIST_FILE_NAME } from "../constants";

interface UseShopListInitParams {
  chainViewFlag: boolean;
  chainListItems: Ref<Array<ChainItem | null | undefined> | null | undefined>;
  chainSelectValues: Ref<{ chain_code: string; name: string }>;
  shopListInput: ShopListInput;
  shopListItems: Ref<Array<ShopSelect | null | undefined> | null | undefined>;
  ShopNameSelectValues: Ref<Array<{ shop_code: string; name: string }>>;
  searchConditionChainCode: Ref<string | undefined | null>;
  searchConditionShopCode: Ref<string | undefined | null>;
  searchConditionPhone: Ref<string | undefined | null>;
  searchConditionZipCode: Ref<string | undefined | null>;
  searchConditionAddress: Ref<string | undefined | null>;
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  initializeTableHeaders: () => void;
  onClickSearch: () => Promise<void>;
  listOptions: { page: number; itemsPerPage: number };
}

/**
 * 画面表示時に必要な初期化処理を提供する composable。
 *
 * @returns 初期化処理を実行する関数群と初期表示に必要な状態
 */
export const useShopListInit = (params: UseShopListInitParams) => {
  const {
    chainViewFlag,
    chainListItems,
    chainSelectValues,
    shopListInput,
    shopListItems,
    ShopNameSelectValues,
    searchConditionChainCode,
    searchConditionShopCode,
    searchConditionPhone,
    searchConditionZipCode,
    searchConditionAddress,
    errdialog,
    errMessage,
    initializeTableHeaders,
    onClickSearch,
    listOptions,
  } = params;

  const wordList = ref<Record<string, string>>({});

  // 初期表示に必要な最小限のラベルキーのみ（OperatorRegister.vueと同様のパターン）
  const requiredFieldLabelKeys = [
    "lbl_shop_list",
    "lbl_search",
    "lbl_chain_name",
    "lbl_shop_code",
    "lbl_shop_name",
    "plc_selectbox",
    "lbl_no_data_text",
  ] as const;

  const isWordListReady = computed(() => {
    if (Object.keys(wordList.value).length === 0) return false;
    return requiredFieldLabelKeys.every((key) => key in wordList.value);
  });

  /**
   * 初期ロード処理（onMountedから呼び出す）
   */
  const runInitialLoad = async (
    route: { query: Record<string, unknown> }
  ): Promise<void> => {
    try {
      //初期化処理
      initializeTableHeaders();

      /**
       * 文言データ取得処理
       */
      const wl = await getWordList(
        localStorageGetChainCode(),
        SHOP_LIST_FILE_NAME,
        localStorageGetCustomerCode()
      );
      Object.assign(wordList.value, wl);

      //運用管理者以外の場合、チェーンコードはログインユーザのチェーンコードにする
      if (chainViewFlag) {
        //チェーン情報を取得する
        chainListItems.value = await getChainList(
          localStorageGetCustomerCode()
        );
        searchConditionChainCode.value = "";
        shopListInput.chain_code = "";
      } else {
        //チェーン情報を設定する
        chainSelectValues.value.chain_code = localStorageGetChainCode();
        searchConditionChainCode.value = localStorageGetChainCode();
        shopListInput.chain_code = localStorageGetChainCode();

        //店舗情報を取得する
        shopListItems.value = await getShopList(shopListInput);
      }

      //編集画面からの戻り（Vue Router 4ではルート定義にないparamsは渡せないためqueryを使用）
      if (
        route.query !== null &&
        route.query !== undefined &&
        route.query.searchMode
      ) {
        //検索条件を取得する
        const searchCondition: Map<string, string> =
          await searchConditionStore.getCondition(SHOP_LIST_FILE_NAME);
        const searchCondition_Name: Map<string, string> =
          await searchConditionStore.getCondition("ShopName");

        //検索条件を削除する
        await searchConditionStore.clearCondition(SHOP_LIST_FILE_NAME);
        await searchConditionStore.clearCondition("ShopName");

        if (searchCondition !== null && searchCondition !== undefined) {
          //チェーン情報を設定する
          searchConditionChainCode.value = searchCondition.get("chain_code");
          const wk_chain_name = searchCondition.get("chain_name");
          chainSelectValues.value = {
            chain_code: searchConditionChainCode.value
              ? searchConditionChainCode.value
              : "",
            name: wk_chain_name ? wk_chain_name : "",
          };
          //店舗情報を設定する
          shopListInput.chain_code = searchConditionChainCode.value ?? "";
          shopListItems.value = await getShopList(shopListInput);
          searchConditionShopCode.value = searchCondition.get("shop_code");
          searchConditionPhone.value = searchCondition.get("phone_number");
          searchConditionZipCode.value = searchCondition.get("zip_code");
          searchConditionAddress.value = searchCondition.get("address");

          if (
            searchCondition_Name !== null &&
            searchCondition_Name !== undefined
          ) {
            //店舗名を設定する
            ShopNameSelectValues.value = [];
            searchCondition_Name.forEach((value, key) => {
              if (key !== null && key !== undefined) {
                ShopNameSelectValues.value.push({
                  shop_code: key,
                  name: value,
                });
              }
            });
          }

          //検索
          // ページネーションを復元する
          // onClickSearch() はページを1にリセットするため、
          // 保存ページが2以上の場合は listOptions.page を直接設定し watch 経由で検索する
          const savedPage = searchCondition.get("page");
          if (savedPage && Number(savedPage) > 1) {
            listOptions.page = Number(savedPage);
          } else {
            await onClickSearch();
          }
        }
      }
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      if (err instanceof Error) {
        errMessage.value = err.message;
      } else {
        errMessage.value = "予期しないエラーが発生しました。";
      }
      return;
    }
  };

  return {
    wordList,
    isWordListReady,
    runInitialLoad,
  };
};
